import React from 'react';
import './ProgressBar.css';

/**
 * ProgressBar component for displaying loading progress
 * Based on ArbiWin design system
 *
 * @param {Object} props - ProgressBar properties
 * @param {number} props.value - Progress value (0-100)
 * @param {number} props.max - Maximum value (default: 100)
 * @param {string} props.variant - Progress variant: 'default', 'success', 'warning', 'error', 'primary'
 * @param {string} props.size - Progress size: 'small', 'medium', 'large'
 * @param {boolean} props.showLabel - Whether to show progress label
 * @param {string} props.label - Custom label text
 * @param {boolean} props.showPercentage - Whether to show percentage
 * @param {boolean} props.indeterminate - Whether progress is indeterminate (loading animation)
 * @param {boolean} props.striped - Whether to show striped pattern
 * @param {boolean} props.animated - Whether to animate stripes
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} ProgressBar component
 */
const ProgressBar = function ProgressBar({
  value = 0,
  max = 100,
  variant = 'default',
  size = 'medium',
  showLabel = false,
  label,
  showPercentage = false,
  indeterminate = false,
  striped = false,
  animated = false,
  className = '',
  testId
}) {

  const progressClasses = [
    'arbiwin-progress-bar',
    'arbiwin-progress-bar--' + variant,
    'arbiwin-progress-bar--' + size,
    indeterminate ? 'arbiwin-progress-bar--indeterminate' : '',
    striped ? 'arbiwin-progress-bar--striped' : '',
    animated ? 'arbiwin-progress-bar--animated' : '',
    showLabel ? 'arbiwin-progress-bar--with-label' : '',
    className
  ].filter(Boolean).join(' ');

  const percentage = indeterminate ? 0 : Math.min(Math.max((value / max) * 100, 0), 100);
  const displayLabel = label || (showPercentage ? `${Math.round(percentage)}%` : '');

  return (
    <div
      className={progressClasses}
      data-testid={testId || 'arbiwin-progress-bar'}
    >
      {showLabel && displayLabel && (
        <div className="arbiwin-progress-bar__label">
          <span className="arbiwin-progress-bar__label-text">
            {displayLabel}
          </span>
        </div>
      )}

      <div
        className="arbiwin-progress-bar__track"
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemin="0"
        aria-valuemax={max}
        aria-label={displayLabel || 'Progress'}
      >
        <div
          className="arbiwin-progress-bar__fill"
          style={indeterminate ? {} : { width: percentage + '%' }}
        />
      </div>
    </div>
  );
};

/**
 * CircularProgress component for circular progress display
 *
 * @param {Object} props - CircularProgress properties
 * @param {number} props.value - Progress value (0-100)
 * @param {number} props.max - Maximum value (default: 100)
 * @param {string} props.variant - Progress variant
 * @param {string} props.size - Progress size: 'small', 'medium', 'large'
 * @param {number} props.strokeWidth - Stroke width for the progress circle
 * @param {boolean} props.showLabel - Whether to show progress label
 * @param {string} props.label - Custom label text
 * @param {boolean} props.showPercentage - Whether to show percentage
 * @param {boolean} props.indeterminate - Whether progress is indeterminate
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} CircularProgress component
 */
const CircularProgress = function CircularProgress({
  value = 0,
  max = 100,
  variant = 'default',
  size = 'medium',
  strokeWidth = 4,
  showLabel = false,
  label,
  showPercentage = false,
  indeterminate = false,
  className = '',
  testId
}) {

  const circularClasses = [
    'arbiwin-circular-progress',
    'arbiwin-circular-progress--' + variant,
    'arbiwin-circular-progress--' + size,
    indeterminate ? 'arbiwin-circular-progress--indeterminate' : '',
    className
  ].filter(Boolean).join(' ');

  const sizeMap = {
    small: 32,
    medium: 48,
    large: 64
  };

  const circleSize = sizeMap[size] || sizeMap.medium;
  const radius = (circleSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const percentage = indeterminate ? 0 : Math.min(Math.max((value / max) * 100, 0), 100);
  const strokeDashoffset = indeterminate ? 0 : circumference - (percentage / 100) * circumference;
  const displayLabel = label || (showPercentage ? `${Math.round(percentage)}%` : '');

  return (
    <div
      className={circularClasses}
      data-testid={testId || 'arbiwin-circular-progress'}
    >
      <svg
        className="arbiwin-circular-progress__svg"
        width={circleSize}
        height={circleSize}
        role="progressbar"
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuemin="0"
        aria-valuemax={max}
        aria-label={displayLabel || 'Progress'}
      >
        {/* Background circle */}
        <circle
          className="arbiwin-circular-progress__background"
          cx={circleSize / 2}
          cy={circleSize / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Progress circle */}
        <circle
          className="arbiwin-circular-progress__progress"
          cx={circleSize / 2}
          cy={circleSize / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${circleSize / 2} ${circleSize / 2})`}
        />
      </svg>

      {showLabel && displayLabel && (
        <div className="arbiwin-circular-progress__label">
          <span className="arbiwin-circular-progress__label-text">
            {displayLabel}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * LoadingProgress component - Pre-configured for loading states
 *
 * @param {Object} props - LoadingProgress properties
 * @param {string} props.message - Loading message
 * @param {string} props.variant - Progress variant
 * @param {string} props.size - Progress size
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} LoadingProgress component
 */
const LoadingProgress = function LoadingProgress({
  message = 'Зареждане...',
  variant = 'primary',
  size = 'medium',
  className = ''
}) {
  return (
    <div className={'arbiwin-loading-progress ' + className}>
      <ProgressBar
        indeterminate
        variant={variant}
        size={size}
        showLabel={!!message}
        label={message}
        striped
        animated
      />
    </div>
  );
};

/**
 * UploadProgress component - Pre-configured for file uploads
 *
 * @param {Object} props - UploadProgress properties
 * @param {number} props.progress - Upload progress (0-100)
 * @param {string} props.fileName - Name of file being uploaded
 * @param {string} props.status - Upload status: 'uploading', 'success', 'error'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} UploadProgress component
 */
const UploadProgress = function UploadProgress({
  progress = 0,
  fileName,
  status = 'uploading',
  className = ''
}) {

  const getVariant = function() {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      default: return 'primary';
    }
  };

  const getMessage = function() {
    switch (status) {
      case 'success': return 'Качването завърши успешно';
      case 'error': return 'Грешка при качване';
      default: return fileName ? `Качване на ${fileName}...` : 'Качване...';
    }
  };

  return (
    <div className={'arbiwin-upload-progress ' + className}>
      <ProgressBar
        value={progress}
        variant={getVariant()}
        size="medium"
        showLabel
        label={getMessage()}
        showPercentage={status === 'uploading'}
        striped={status === 'uploading'}
        animated={status === 'uploading'}
      />
    </div>
  );
};

ProgressBar.displayName = 'ProgressBar';
CircularProgress.displayName = 'CircularProgress';
LoadingProgress.displayName = 'LoadingProgress';
UploadProgress.displayName = 'UploadProgress';

export default ProgressBar;
export { CircularProgress, LoadingProgress, UploadProgress };
